import { _PayloadColumn } from './columns';

export type SchemaColumnRef = {
  name: string;
  destination: string;
};

export type ColumnDescriptorSource = SchemaColumnRef & {
  description: string | undefined;
  required: boolean;
};

export namespace _VerifyPayload {
  export import Columns = _PayloadColumn;

  /** Customer DB verify UI 向けに schema 列を射影した型。 */
  export type ColumnDescriptor = {
    /** 照合入力で利用する列キー（`customer.<列名>` / `contract.<列名>` 形式）。 */
    columnKey: string;
    /** schema 上の列名（返却列指定で利用）。 */
    name: string;
    /** 列の所属先。 */
    destination: _PayloadColumn.Destination;
    /** 列の説明。 */
    description: string | undefined;
    /** 必須列かどうか。 */
    required: boolean;
    /** 契約一意キーに含まれる列かどうか。 */
    isContractUniqueKey: boolean;
    /** 顧客一意キーに含まれる列かどうか。 */
    isCustomerUniqueKey: boolean;
  };

  /**
   * active revision の schema 列を Customer DB verify UI 向けに射影する。
   * `destination=ignore` の列は除外する。
   */
  export const columnDescriptorsFromSchema = (
    schemaColumns: readonly ColumnDescriptorSource[],
    contractUniqueKeyColumnNames: readonly string[],
    customerUniqueKeyColumnNames: readonly string[] = [],
  ): ColumnDescriptor[] => {
    const contractUniqueKeyColumnNamesSet = new Set(contractUniqueKeyColumnNames);
    const customerUniqueKeyColumnNamesSet = new Set(customerUniqueKeyColumnNames);
    return schemaColumns
      .filter(
        (column): column is ColumnDescriptorSource & { destination: _PayloadColumn.Destination } =>
          column.destination === 'customer' || column.destination === 'contract',
      )
      .map((column) => ({
        columnKey: _PayloadColumn.Format(column.destination, column.name),
        name: column.name,
        destination: column.destination,
        description: column.description,
        required: column.required,
        isContractUniqueKey: column.destination === 'contract' && contractUniqueKeyColumnNamesSet.has(column.name),
        isCustomerUniqueKey: column.destination === 'customer' && customerUniqueKeyColumnNamesSet.has(column.name),
      }));
  };

  /** schema の customer / contract 列を `destination.<列名>` 形式で列挙する。 */
  export const allowedPrefixedColumnsFromSchema = (schemaColumns: readonly SchemaColumnRef[]): string[] =>
    schemaColumns
      .filter((c) => c.destination === 'customer' || c.destination === 'contract')
      .map((c) => _PayloadColumn.Format(c.destination as _PayloadColumn.Destination, c.name));

  /** 契約一意キー列を `contract.<列名>` 形式で列挙する。 */
  export const requiredContractUniqueKeyPrefixedColumns = (contractUniqueKeyColumnNames: readonly string[]): string[] =>
    contractUniqueKeyColumnNames.map((name) => _PayloadColumn.Format('contract', name));

  export type ReturnFields = _PayloadColumn.ReturnFieldsInput;
}
